import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Database, Zap, CheckCircle2, XCircle, Table2, Plus, X } from "lucide-react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const DB_TYPES = [
  { value: "mysql", label: "MySQL", port: 3306 },
  { value: "postgresql", label: "PostgreSQL", port: 5432 },
  { value: "mongodb", label: "MongoDB (internal)", port: 27017 },
  { value: "mongodb_external", label: "MongoDB (eksternal)", port: 27017 },
];

function CustomTablesEditor({ tables, onChange }) {
  const [val, setVal] = useState("");
  const addTokens = (raw) => {
    const parts = raw.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const merged = [...tables];
    for (const p of parts) if (!merged.includes(p)) merged.push(p);
    onChange(merged.slice(0, 10));
  };
  return (
    <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-3" data-testid="custom-tables-editor">
      <div className="flex gap-2">
        <Input data-testid="input-custom-table" value={val}
          placeholder="mis. barang, stok_gudang — pisahkan koma / Enter"
          onChange={(e) => {
            if (e.target.value.includes(",")) { addTokens(e.target.value); setVal(""); }
            else setVal(e.target.value);
          }}
          onKeyDown={(e) => { if (e.key === "Enter" && val.trim()) { e.preventDefault(); addTokens(val); setVal(""); } }} />
        <Button type="button" variant="outline" size="icon" data-testid="btn-add-custom-table"
          onClick={() => { if (val.trim()) { addTokens(val); setVal(""); } }}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      {tables.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tables.map((t, i) => (
            <span key={t} data-testid={`custom-table-chip-${i}`}
              className="text-xs font-mono bg-secondary rounded-full pl-3 pr-1.5 py-1 flex items-center gap-1">
              {t}
              <button type="button" data-testid={`btn-remove-custom-table-${i}`}
                onClick={() => onChange(tables.filter((x) => x !== t))}
                className="hover:text-destructive"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Nama tabel ini dibaca chatbot sebagai konteks jawaban (maks. 10 tabel, 50 baris pertama per tabel).
        Hanya huruf, angka, dan underscore.
      </p>
    </div>
  );
}

export default function DatabaseHub({ business, onUpdated }) {
  const [cfg, setCfg] = useState(business.db_config);
  const [testing, setTesting] = useState(false);
  const [schema, setSchema] = useState([]);

  useEffect(() => { setCfg(business.db_config); setSchema([]); }, [business.id]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const saveCfg = async () => {
    const { data } = await api.put(`/businesses/${business.id}/db-config`, {
      type: cfg.type, host: cfg.host || "", port: cfg.port || 3306,
      db_name: cfg.db_name || "", username: cfg.username || "",
      password: cfg.password || "", ssl: cfg.ssl ?? true, uri: cfg.uri || "",
      woocommerce_mode: cfg.woocommerce_mode ?? false, auth_source: cfg.auth_source || "",
      table_structure: cfg.table_structure || "standard",
      custom_tables: cfg.custom_tables || [],
    });
    onUpdated(data);
    return data;
  };

  const test = async () => {
    if ((cfg.table_structure || "standard") === "custom" && !(cfg.custom_tables || []).length) {
      toast.error("Struktur Custom dipilih — isi minimal satu nama tabel");
      return;
    }
    setTesting(true);
    try {
      await saveCfg();
      const { data } = await api.post(`/businesses/${business.id}/test-db-connection`);
      setSchema(data.schema || []);
      if (data.status === "connected") toast.success(data.message);
      else toast.error(data.message);
      const fresh = await api.get(`/businesses/${business.id}`);
      onUpdated(fresh.data);
      setCfg(fresh.data.db_config);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  const status = cfg.status;
  const statusMap = {
    connected: { icon: CheckCircle2, cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20", txt: "Terhubung" },
    failed: { icon: XCircle, cls: "bg-destructive/10 text-destructive border-destructive/20", txt: "Gagal" },
    not_configured: { icon: Database, cls: "bg-muted text-muted-foreground border-border", txt: "Belum dikonfigurasi" },
  };
  const S = statusMap[status] || statusMap.not_configured;

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-5 rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-semibold flex items-center gap-2"><Database className="w-4 h-4" /> Koneksi Database Bisnis</h3>
          <Badge data-testid="badge-db-connection-status" variant="outline" className={S.cls}>
            <S.icon className="w-3.5 h-3.5 mr-1" /> {S.txt}
          </Badge>
        </div>

        <div className="space-y-2">
          <Label>Jenis Database</Label>
          <Select value={cfg.type} onValueChange={(v) => {
            const t = DB_TYPES.find((d) => d.value === v);
            setCfg((c) => ({ ...c, type: v, port: t.port, auth_source: v === "mongodb_external" ? (c.auth_source || "admin") : c.auth_source }));
          }}>
            <SelectTrigger data-testid="select-db-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DB_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {cfg.type === "mysql" && (
          <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-muted/40 p-3">
            <div className="space-y-0.5">
              <Label htmlFor="wc-mode-toggle">Mode WooCommerce (WordPress)</Label>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Baca produk, pesanan &amp; info toko dari tabel <code className="font-mono">wp_posts</code>, <code className="font-mono">wp_postmeta</code> &amp; <code className="font-mono">wp_options</code>. Prefix tabel terdeteksi otomatis.
              </p>
            </div>
            <Switch id="wc-mode-toggle" data-testid="toggle-woocommerce-mode"
              checked={cfg.woocommerce_mode ?? false}
              onCheckedChange={(v) => set("woocommerce_mode", v)} />
          </div>
        )}

        {(cfg.type === "mysql" || cfg.type === "postgresql") && !cfg.woocommerce_mode && (
          <div className="space-y-2">
            <Label>Struktur Tabel</Label>
            <Select value={cfg.table_structure || "standard"} onValueChange={(v) => set("table_structure", v)}>
              <SelectTrigger data-testid="select-table-structure"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standard" data-testid="table-structure-standard">Standard (products/orders)</SelectItem>
                <SelectItem value="custom" data-testid="table-structure-custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            {cfg.table_structure === "custom" && (
              <CustomTablesEditor tables={cfg.custom_tables || []} onChange={(v) => set("custom_tables", v)} />
            )}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Host</Label>
            <Input data-testid="input-db-host" value={cfg.host || ""} placeholder="db.example.com"
              onChange={(e) => set("host", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Port</Label>
            <Input type="number" value={cfg.port || ""} onChange={(e) => set("port", Number(e.target.value))} />
          </div>
          <div className="space-y-2">
            <Label>Nama Database</Label>
            <Input data-testid="input-db-name" value={cfg.db_name || ""} placeholder="shop_db"
              onChange={(e) => set("db_name", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Username</Label>
            <Input value={cfg.username || ""} onChange={(e) => set("username", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Password</Label>
            <Input data-testid="input-db-password" type="password" value={cfg.password || ""}
              placeholder={cfg.type === "mongodb" ? "(tidak diperlukan)" : "••••••••"}
              onChange={(e) => set("password", e.target.value)} />
          </div>
        </div>
        {cfg.type === "mongodb_external" && (
          <div className="space-y-2">
            <Label>Auth Source (opsional)</Label>
            <Input className="font-mono text-xs" data-testid="input-db-auth-source" value={cfg.auth_source || ""}
              placeholder="admin" onChange={(e) => set("auth_source", e.target.value)} />
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Database tempat user MongoDB dibuat — umumnya <code className="font-mono">admin</code>.
            </p>
          </div>
        )}
        <div className="space-y-2">
          <Label>Atau Connection URI (opsional)</Label>
          <Input className="font-mono text-xs" value={cfg.uri || ""}
            placeholder={
              cfg.type === "mongodb_external" ? "mongodb+srv://user:pass@host/db"
                : cfg.type === "mysql" ? "mysql://user:pass@host:3306/db"
                  : "postgresql://user:pass@host:5432/db"
            }
            onChange={(e) => set("uri", e.target.value)} />
        </div>

        <div className="rounded-lg bg-muted/60 p-3 text-[11px] text-muted-foreground leading-relaxed">
          {cfg.type === "mysql" && cfg.woocommerce_mode ? (
            <>Mode WooCommerce aktif: produk dibaca dari <code className="font-mono">wp_posts</code> (post_type='product') + <code className="font-mono">wp_postmeta</code> (harga, stok, SKU),
            pesanan dari <code className="font-mono">post_type='shop_order'</code>, dan info toko dari <code className="font-mono">wp_options</code> (siteurl, blogname).</>
          ) : cfg.type === "mongodb_external" ? (
            <>Koleksi MongoDB yang dibaca chatbot: <code className="font-mono">products(name, price, stock, category, description)</code> &amp;
            <code className="font-mono"> orders(order_code, customer_name, status, total, items)</code>.</>
          ) : (cfg.table_structure === "custom" && (cfg.type === "mysql" || cfg.type === "postgresql")) ? (
            <>Struktur <b>Custom</b> aktif: chatbot membaca {(cfg.custom_tables || []).length > 0
              ? <>{cfg.custom_tables.length} tabel yang Anda tentukan ({cfg.custom_tables.join(", ")})</>
              : <>tabel yang Anda tentukan</>} sebagai konteks jawaban (<code className="font-mono">SELECT *</code>, 50 baris pertama per tabel). Jalankan test koneksi untuk memvalidasi nama tabel.</>
          ) : (
            <>Skema default yang dibaca chatbot: <code className="font-mono">products(name, price, stock, category, description)</code> &amp;
            <code className="font-mono"> orders(order_code, customer_name, status, total, items)</code>.</>
          )}
          {" "}Bila koneksi kosong/gagal, chatbot memakai data internal. Promo <b>selalu</b> dibaca dari data internal, bukan dari database bisnis.
        </div>

        <Button data-testid="btn-test-db-connection" onClick={test} disabled={testing}
          className="bg-accent hover:bg-accent/90 text-white">
          <Zap className="w-4 h-4 mr-1.5" /> {testing ? "Menguji..." : "Test & Simpan Koneksi"}
        </Button>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <h3 className="font-display font-semibold flex items-center gap-2 mb-4"><Table2 className="w-4 h-4" /> Schema Inspector</h3>
        {schema.length === 0 ? (
          <p className="text-sm text-muted-foreground">Jalankan test koneksi untuk memindai tabel/koleksi.</p>
        ) : (
          <div className="space-y-2">
            {schema.map((t) => (
              <div key={t} className="flex items-center gap-2 text-sm rounded-lg bg-muted px-3 py-2 font-mono">
                <Table2 className="w-3.5 h-3.5 text-muted-foreground" /> {t}
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
          MySQL, PostgreSQL &amp; MongoDB (eksternal) diuji koneksi <b>live</b> — chatbot membaca produk &amp; pesanan
          langsung dari database bisnis bila terhubung. Memilih MongoDB (internal) berarti memakai data internal,
          bukan koneksi ke MongoDB eksternal.
        </p>
      </div>
    </div>
  );
}
